import { type ReactNode, useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { cn } from '@/lib/cn';

interface AuthInputProps {
  id: string;
  label: ReactNode;
  type?: 'text' | 'email' | 'password';
  value: string;
  onChange: (v: string) => void;
  error?: string | undefined;
  required?: boolean;
  autoComplete?: string;
  placeholder?: string;
  disabled?: boolean;
  /** Render extra content (e.g. a status icon) at the right edge of the input. */
  adornment?: ReactNode;
}

/** Themed text input with label, error, optional password eye toggle. */
export function AuthInput({
  id,
  label,
  type = 'text',
  value,
  onChange,
  error,
  required,
  autoComplete,
  placeholder,
  disabled,
  adornment,
}: AuthInputProps) {
  const [show, setShow] = useState(false);
  const isPassword = type === 'password';

  return (
    <div className="space-y-1.5">
      {label ? (
        <label htmlFor={id} className="block text-xs font-medium text-foreground">
          {label}
          {required && (
            <span className="ml-0.5 text-destructive" aria-hidden>
              *
            </span>
          )}
        </label>
      ) : null}
      <div className="relative">
        <input
          id={id}
          type={isPassword && show ? 'text' : type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          autoComplete={autoComplete}
          placeholder={placeholder}
          disabled={disabled}
          aria-invalid={error ? true : undefined}
          className={cn(
            'h-10 w-full rounded-lg border bg-background px-3 text-sm text-foreground outline-none transition-colors placeholder:text-muted-foreground/60 focus:ring-2',
            (isPassword || adornment) && 'pr-10',
            disabled && 'opacity-70',
            error
              ? 'border-destructive focus:border-destructive focus:ring-destructive/20'
              : 'border-border focus:border-foreground focus:ring-foreground/10',
          )}
        />
        {isPassword ? (
          <button
            type="button"
            tabIndex={-1}
            onClick={() => setShow((s) => !s)}
            aria-label={show ? 'Hide password' : 'Show password'}
            className="absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground transition-colors hover:text-foreground"
          >
            {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        ) : adornment ? (
          <div className="absolute inset-y-0 right-0 flex items-center px-3">{adornment}</div>
        ) : null}
      </div>
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}
